/**
 * InviteFriendSheet Component
 *
 * Bottom sheet friend picker for list invitations.
 * Shows the user's friends list with role picker (editor/viewer).
 */

import { userFriendsQueryOptions } from '@/api/queries/friendship';
import { Modal } from '@/components/ui/modal';
import { useAuth } from '@/hooks/use-auth';
import { imageUrl } from '@/utils/image-url';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { ActivityIndicator, Image, Pressable, ScrollView, Text, View } from 'react-native';
import { useInviteListMember } from '../hooks/use-list-member-mutations';

interface InviteFriendSheetProps {
  listId: string;
  visible: boolean;
  onClose: () => void;
}

type MemberRole = 'editor' | 'viewer';

export function InviteFriendSheet({ listId, visible, onClose }: InviteFriendSheetProps) {
  const { user } = useAuth();
  const [selectedRole, setSelectedRole] = useState<MemberRole>('editor');
  const [invitedIds, setInvitedIds] = useState<Set<string>>(new Set());

  const { data: friends, isLoading } = useQuery({
    ...userFriendsQueryOptions(user?.id ?? ''),
    enabled: visible && !!user?.id,
  });

  const inviteMember = useInviteListMember();

  const handleInvite = (friendUserId: string) => {
    inviteMember.mutate(
      {
        listId,
        data: { user_id: friendUserId, role: selectedRole },
      },
      {
        onSuccess: () => {
          setInvitedIds((prev) => new Set(prev).add(friendUserId));
        },
      }
    );
  };

  const handleClose = () => {
    setInvitedIds(new Set());
    onClose();
  };

  return (
    <Modal visible={visible} onClose={handleClose}>
      <View className="bg-background rounded-xl" style={{ maxHeight: 500 }}>
        <Modal.Header>
          <View className="flex-row items-center justify-between">
            <Text className="text-lg font-bold text-foreground">
              Invite Friend
            </Text>
            <Pressable
              onPress={handleClose}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              accessibilityRole="button"
              accessibilityLabel="Close"
            >
              <Ionicons name="close" size={24} className="text-muted-foreground" />
            </Pressable>
          </View>

          {/* Role picker */}
          <View className="flex-row mt-3" style={{ gap: 8 }}>
            <Pressable
              onPress={() => setSelectedRole('editor')}
              className={`flex-1 items-center py-2 rounded-lg border ${
                selectedRole === 'editor'
                  ? 'border-primary bg-primary/10'
                  : 'border-border bg-surface'
              }`}
              accessibilityRole="radio"
              accessibilityState={{ selected: selectedRole === 'editor' }}
            >
              <Text
                className={`text-sm font-medium ${
                  selectedRole === 'editor' ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                Editor
              </Text>
            </Pressable>
            <Pressable
              onPress={() => setSelectedRole('viewer')}
              className={`flex-1 items-center py-2 rounded-lg border ${
                selectedRole === 'viewer'
                  ? 'border-primary bg-primary/10'
                  : 'border-border bg-surface'
              }`}
              accessibilityRole="radio"
              accessibilityState={{ selected: selectedRole === 'viewer' }}
            >
              <Text
                className={`text-sm font-medium ${
                  selectedRole === 'viewer' ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                Viewer
              </Text>
            </Pressable>
          </View>
        </Modal.Header>

        {isLoading ? (
          <View className="py-12 items-center">
            <ActivityIndicator />
            <Text className="mt-2 text-sm text-muted-foreground">
              Loading friends...
            </Text>
          </View>
        ) : (
          <ScrollView
            style={{ maxHeight: 320 }}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ padding: 24 }}
          >
            {(!friends || friends.length === 0) ? (
              <View className="items-center py-8">
                <Text className="text-muted-foreground">
                  No friends to invite. Add friends first.
                </Text>
              </View>
            ) : (
              friends.map((friend) => {
                const avatarUrl = friend.user.image
                  ? imageUrl(friend.user.image, 'thumbnail')
                  : null;
                const isInvited = invitedIds.has(friend.friendId);
                const isInviting =
                  inviteMember.isPending &&
                  inviteMember.variables?.data.user_id === friend.friendId;

                return (
                  <View
                    key={friend.id}
                    className="flex-row items-center py-3 border-b border-border"
                  >
                    {/* Avatar */}
                    {avatarUrl ? (
                      <Image
                        source={{ uri: avatarUrl }}
                        className="w-10 h-10 rounded-full"
                        accessibilityLabel={`${friend.user.name || 'Friend'}'s profile photo`}
                      />
                    ) : (
                      <View className="w-10 h-10 rounded-full items-center justify-center bg-surface-secondary">
                        <Ionicons name="person" size={20} className="text-muted-foreground" />
                      </View>
                    )}

                    {/* Name */}
                    <View className="flex-1 ml-3">
                      <Text className="font-medium text-foreground" numberOfLines={1}>
                        {friend.user.name || 'User'}
                      </Text>
                      {friend.user.username && (
                        <Text className="text-xs text-muted-foreground" numberOfLines={1}>
                          @{friend.user.username}
                        </Text>
                      )}
                    </View>

                    {/* Invite button */}
                    {isInvited ? (
                      <View className="flex-row items-center">
                        <Ionicons name="checkmark-circle" size={20} className="text-success" />
                        <Text className="ml-1 text-sm text-success">Invited</Text>
                      </View>
                    ) : (
                      <Pressable
                        onPress={() => handleInvite(friend.friendId)}
                        disabled={isInviting}
                        className="px-3 py-1.5 rounded-lg bg-primary"
                        accessibilityRole="button"
                        accessibilityLabel={`Invite ${friend.user.name || 'friend'}`}
                        style={{ opacity: isInviting ? 0.5 : 1 }}
                      >
                        {isInviting ? (
                          <ActivityIndicator size="small" color="#fff" />
                        ) : (
                          <Text className="text-sm font-medium text-primary-foreground">
                            Invite
                          </Text>
                        )}
                      </Pressable>
                    )}
                  </View>
                );
              })
            )}
          </ScrollView>
        )}
      </View>
    </Modal>
  );
}
